import { Flex, Text } from '@mantine/core';
import {
	ScheduledEmailCompletionReason,
	ScheduledEmailStatus,
	type ScheduledEmailRes,
} from '@dealstack/shared/lib/backendClient';
import {
	formatStringDateToRelative,
	formatStringDateToUI,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { StatusBadge } from '@dealstack/frontend/shared/components/StatusBadge/StatusBadge';
import { TableRowActionsMenu } from '@dealstack/frontend/shared/components/TableRowActionsMenu/TableRowActionsMenu';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import type {
	GetScheduledQueueColumnsParams,
	ScheduledQueueBadgeVariant,
} from './getScheduledQueueColumns.types';

// The generated `lastPendingCount?: number | null` (see ScheduledEmailRes) can be
// null - read it safely without a banned type-cast.
export const getLastPendingCount = (value: unknown): number =>
	typeof value === 'number' ? value : 0;

export const isCapReached = (schedule: ScheduledEmailRes): boolean =>
	schedule.status === ScheduledEmailStatus.COMPLETED &&
	schedule.completionReason === ScheduledEmailCompletionReason.MAX_FIRES_REACHED &&
	getLastPendingCount(schedule.lastPendingCount) > 0;

export const getScheduledQueueBadgeVariant = (
	schedule: ScheduledEmailRes
): ScheduledQueueBadgeVariant => {
	if (schedule.status === ScheduledEmailStatus.ACTIVE) {
		return 'active';
	}
	// PROCESSING is a mid-flight sweep claim; surface it as active, not the COMPLETED
	// fall-through below (which would render a still-running row as "Completed").
	if (schedule.status === ScheduledEmailStatus.PROCESSING) {
		return 'active';
	}
	if (schedule.status === ScheduledEmailStatus.CANCELLED) {
		return 'cancelled';
	}
	if (schedule.status === ScheduledEmailStatus.FAILED) {
		return 'failed';
	}
	if (
		schedule.status === ScheduledEmailStatus.COMPLETED &&
		schedule.completionReason === ScheduledEmailCompletionReason.END_DATE_REACHED
	) {
		return 'ended';
	}
	// COMPLETED
	return isCapReached(schedule) ? 'cap-reached' : 'completed';
};

export const BADGE_VARIANT_COLOR: Record<ScheduledQueueBadgeVariant, string> = {
	active: 'teal.8',
	'cap-reached': 'orange.6',
	completed: 'gray.5',
	cancelled: 'gray.5',
	failed: 'red.7',
	ended: 'gray.6',
};

export const getScheduledQueueColumns = ({
	tMipManage,
	handlers,
}: GetScheduledQueueColumnsParams): ExtendedColumnDef<ScheduledEmailRes>[] => {
	const { onSendNow, onReschedule, onCancel, onRestart } = handlers;

	return [
		{
			id: 'target',
			accessorFn: (row) => row.targetName ?? row.documentWorkflowInstanceId,
			header: tMipManage('automated_emails.scheduled_queue.columns.target'),
			columnLabel: tMipManage('automated_emails.scheduled_queue.columns.target'),
			enableGlobalFilter: true,
			enableSorting: true,
			cell: ({ row }) => (
				<Text size="sm" fw={500} truncate lineClamp={1}>
					{row.original.targetName ?? row.original.documentWorkflowInstanceId}
				</Text>
			),
		},
		{
			id: 'type',
			accessorKey: 'type',
			header: tMipManage('automated_emails.scheduled_queue.columns.type'),
			columnLabel: tMipManage('automated_emails.scheduled_queue.columns.type'),
			enableGlobalFilter: false,
			enableSorting: true,
			cell: ({ row }) => (
				<Text size="sm">
					{tMipManage(
						`automated_emails.configuration.${row.original.type.toLowerCase()}.title`
					)}
				</Text>
			),
		},
		{
			id: 'nextFireAt',
			accessorKey: 'nextFireAt',
			header: tMipManage('automated_emails.scheduled_queue.columns.next_send'),
			columnLabel: tMipManage('automated_emails.scheduled_queue.columns.next_send'),
			enableGlobalFilter: false,
			enableSorting: true,
			cell: ({ row }) => {
				if (row.original.status !== ScheduledEmailStatus.ACTIVE) {
					return <Text size="sm">{LONG_DASH}</Text>;
				}
				const relative = formatStringDateToRelative(row.original.nextFireAt);
				const absolute = formatStringDateToUI(row.original.nextFireAt);
				return (
					<Flex direction="column" gap={0}>
						<Text size="sm">{relative ?? LONG_DASH}</Text>
						<Text size="xs" c="dark.0">
							{absolute}
						</Text>
					</Flex>
				);
			},
		},
		{
			id: 'progress',
			accessorFn: (row) => `${row.fireCount}/${row.maxFires}`,
			header: tMipManage('automated_emails.scheduled_queue.columns.progress'),
			columnLabel: tMipManage('automated_emails.scheduled_queue.columns.progress'),
			enableGlobalFilter: false,
			enableSorting: false,
			cell: ({ row }) => (
				<Text size="sm">
					{row.original.fireCount}/{row.original.maxFires}
				</Text>
			),
		},
		{
			id: 'lastPendingCount',
			accessorFn: (row) => getLastPendingCount(row.lastPendingCount),
			header: tMipManage('automated_emails.scheduled_queue.columns.still_pending'),
			columnLabel: tMipManage('automated_emails.scheduled_queue.columns.still_pending'),
			enableGlobalFilter: false,
			enableSorting: true,
			cell: ({ row }) => (
				<Text size="sm">{getLastPendingCount(row.original.lastPendingCount)}</Text>
			),
		},
		{
			id: 'status',
			accessorKey: 'status',
			header: tMipManage('automated_emails.scheduled_queue.columns.status'),
			columnLabel: tMipManage('automated_emails.scheduled_queue.columns.status'),
			enableGlobalFilter: false,
			enableSorting: false,
			cell: ({ row }) => {
				const variant = getScheduledQueueBadgeVariant(row.original);
				const label = tMipManage(
					`automated_emails.scheduled_queue.status_badge.${variant}`
				);
				return <StatusBadge text={label} color={BADGE_VARIANT_COLOR[variant]} />;
			},
		},
		{
			id: 'row-actions',
			header: '',
			enableSorting: false,
			enableGlobalFilter: false,
			enableColumnFilter: false,
			minSize: 40,
			size: 40,
			cell: ({ row }) => {
				const schedule = row.original;

				if (schedule.status === ScheduledEmailStatus.ACTIVE) {
					return (
						<div onClick={(e) => e.stopPropagation()}>
							<TableRowActionsMenu
								menuTestId={`scheduled-queue-row-actions-${schedule.id}`}
								options={[
									{
										label: tMipManage(
											'automated_emails.scheduled_queue.actions.send_now'
										),
										onClick: () => onSendNow(schedule),
									},
									{
										label: tMipManage(
											'automated_emails.scheduled_queue.actions.reschedule'
										),
										onClick: () => onReschedule(schedule),
									},
									{
										label: tMipManage(
											'automated_emails.scheduled_queue.actions.cancel'
										),
										onClick: () => onCancel(schedule),
									},
								]}
							/>
						</div>
					);
				}

				if (isCapReached(schedule)) {
					return (
						<div onClick={(e) => e.stopPropagation()}>
							<TableRowActionsMenu
								menuTestId={`scheduled-queue-row-actions-${schedule.id}`}
								options={[
									{
										label: tMipManage(
											'automated_emails.scheduled_queue.actions.restart'
										),
										onClick: () => onRestart(schedule),
									},
								]}
							/>
						</div>
					);
				}

				return null;
			},
		},
	];
};
